import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import {
  createAnalyticsExport,
  enableVendorDirectoryAnalytics,
  getCoverageAnalyticsDashboard,
  getExecutiveAnalyticsDashboard,
  getLeadAnalyticsDashboard,
  getQueueAnalyticsDashboard,
} from "../../api/clientVendorDirectoryAnalyticsApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { useVendorDirectoryPermissions } from "../../hooks/useVendorDirectoryPermissions";
import {
  VENDOR_DIRECTORY_PERMISSIONS as P,
  hasVendorDirectoryPermission,
} from "../../auth/casamapaVendorDirectoryPermissions";
import { useLocale } from "../../intl";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.analyticsPage";
const THIS_VIEW = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.thisView";
const ANALYTICS_DISABLED = "ANALYTICS_FEATURE_DISABLED";

const QUEUE_PATHS = {
  candidate_review: "candidatos",
  duplicate_review: "duplicados",
  verification: "verificacion",
  credentials: "credenciales",
  claims: "reclamaciones",
  privacy_requests: "privacidad",
  lead_delivery_failures: "analytics/solicitudes",
  ranking_run_failures: "posicionamiento/ejecuciones",
};

const WARNING_PATHS = {
  coverage_below_target: "expansion",
  lead_delivery_failures: "analytics/solicitudes",
  ranking_run_failures: "posicionamiento/ejecuciones",
};

function translateKey(t, key, fallback) {
  const value = t(key);
  return value !== key ? value : fallback;
}

function translateEnum(t, group, value, fallback) {
  if (!value) return "—";
  return translateKey(t, `${PREFIX}.${group}.${value}`, fallback || formatStatusLabel(value));
}

function isDisabledError(error) {
  return error?.code === ANALYTICS_DISABLED || error?.statusCode === 403;
}

function workspaceHref(serviciosBase, segment) {
  return segment ? `${serviciosBase}/${segment}` : null;
}

function MetricCard({ label, value, hint }) {
  return (
    <div className="cvd-metric-card">
      <div className="cvd-metric-card__label">{label}</div>
      <div className="cvd-metric-card__value">{value ?? "—"}</div>
      {hint ? <div className="cvd-metric-card__hint">{hint}</div> : null}
    </div>
  );
}

function RateCard({ label, rate, t }) {
  if (!rate) return null;
  const insufficient = rate.displayState === "insufficient_sample";
  return (
    <div className="cvd-metric-card">
      <div className="cvd-metric-card__label">{label}</div>
      <div className="cvd-metric-card__value">
        {insufficient
          ? t(`${PREFIX}.sample.insufficient`)
          : rate.percentage != null
            ? `${rate.percentage}%`
            : "—"}
      </div>
      <div className="cvd-metric-card__hint">
        {t(`${PREFIX}.sample.of`, {
          numerator: rate.numerator ?? 0,
          denominator: rate.denominator ?? 0,
        })}
      </div>
    </div>
  );
}

function useViewName() {
  const { t } = useLocale();
  const { activeView } = useOutletContext() || {};
  return activeView?.name || t(THIS_VIEW);
}

function useAnalyticsLoad(loader) {
  const { t } = useLocale();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [disabled, setDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    setDisabled(false);
    try {
      const next = await loader();
      setDashboard(next);
    } catch (error) {
      setDashboard(null);
      if (isDisabledError(error)) {
        setDisabled(true);
      } else {
        setErrorMessage(error.message || t(`${PREFIX}.loadError`));
      }
    } finally {
      setLoading(false);
    }
  }, [loader, t]);

  useEffect(() => {
    load();
  }, [load]);

  return { dashboard, loading, errorMessage, disabled, load };
}

function useEnableAnalytics(onEnabled) {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const { permissions } = useVendorDirectoryPermissions();
  const canEnable = hasVendorDirectoryPermission(permissions, P.SETTINGS_UPDATE);
  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState("");

  const enable = async () => {
    if (!canEnable) return;
    setEnabling(true);
    setEnableError("");
    try {
      await enableVendorDirectoryAnalytics(clientViewId, {
        reason: t(`${PREFIX}.disabled.enableReason`),
      });
      await onEnabled();
    } catch (error) {
      setEnableError(error.message || t(`${PREFIX}.disabled.enableError`));
    } finally {
      setEnabling(false);
    }
  };

  return { canEnable, enabling, enableError, enable };
}

function AnalyticsShell({ children }) {
  const { t } = useLocale();
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const base = `${serviciosBase}/analytics`;

  return (
    <div className="page-content cvd-admin cvd-analytics-hub">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <nav className="cvd-subnav cvd-analytics-hub__subnav" aria-label={t(`${PREFIX}.navLabel`)}>
        <NavLink to={base} end>
          {t(`${PREFIX}.nav.executive`)}
        </NavLink>
        <NavLink to={`${base}/cobertura`}>{t(`${PREFIX}.nav.coverage`)}</NavLink>
        <NavLink to={`${base}/solicitudes`}>{t(`${PREFIX}.nav.leads`)}</NavLink>
        <NavLink to={`${base}/colas`}>{t(`${PREFIX}.nav.queues`)}</NavLink>
      </nav>
      {children}
    </div>
  );
}

function AnalyticsHeader({ title, intro, actions = null }) {
  return (
    <header className={`cvd-analytics-hub__hero${actions ? "" : " cvd-analytics-hub__hero--simple"}`}>
      <div className="cvd-analytics-hub__hero-copy">
        <h1>{title}</h1>
        {intro ? <p>{intro}</p> : null}
      </div>
      {actions}
    </header>
  );
}

function DisabledPanel({ onRetry }) {
  const { t } = useLocale();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { canEnable, enabling, enableError, enable } = useEnableAnalytics(onRetry);
  const nextItems = [
    { key: "executive", to: `${serviciosBase}/analytics` },
    { key: "coverage", to: `${serviciosBase}/analytics/cobertura` },
    { key: "leads", to: `${serviciosBase}/analytics/solicitudes` },
    { key: "queues", to: `${serviciosBase}/analytics/colas` },
  ];

  return (
    <section className="cvd-analytics-hub__disabled" aria-labelledby="cvd-analytics-disabled-title">
      <h2 id="cvd-analytics-disabled-title">{t(`${PREFIX}.disabled.title`)}</h2>
      <p>{t(`${PREFIX}.disabled.body`)}</p>
      <div className="cvd-analytics-hub__disabled-actions">
        {canEnable ? (
          <button type="button" className="btn btn-primary" onClick={enable} disabled={enabling}>
            {enabling ? t(`${PREFIX}.disabled.enabling`) : t(`${PREFIX}.disabled.enable`)}
          </button>
        ) : (
          <p className="cvd-analytics-hub__disabled-note">{t(`${PREFIX}.disabled.askAdmin`)}</p>
        )}
        <Link className="btn" to={`${serviciosBase}/configuracion`}>
          {t(`${PREFIX}.disabled.settings`)}
        </Link>
      </div>
      {enableError ? <p className="cvd-analytics-hub__error">{enableError}</p> : null}
      <h3>{t(`${PREFIX}.disabled.nextTitle`)}</h3>
      <ul className="cvd-analytics-hub__next">
        {nextItems.map((item) => (
          <li key={item.key}>
            <Link to={item.to}>
              <strong>{t(`${PREFIX}.nav.${item.key}`)}</strong>
              <span>{t(`${PREFIX}.disabled.next.${item.key}`)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ActionGuide({ items }) {
  const { t } = useLocale();
  if (!items?.length) return null;
  return (
    <section className="cvd-analytics-hub__guide" aria-labelledby="cvd-analytics-guide-title">
      <h2 id="cvd-analytics-guide-title">{t(`${PREFIX}.actionsTitle`)}</h2>
      <ul>
        {items.map((item) => (
          <li key={item.key}>
            {item.to ? (
              <Link to={item.to}>{item.label}</Link>
            ) : (
              <span>{item.label}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PageState({ loading, errorMessage, disabled, onRetry, children }) {
  const { t } = useLocale();
  if (loading) return <ClientPortalLoading message={t(`${PREFIX}.loading`)} />;
  if (disabled) return <DisabledPanel onRetry={onRetry} />;
  if (errorMessage) return <ClientPortalError message={errorMessage} onRetry={onRetry} />;
  return children;
}

function ExecutiveOverviewPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t } = useLocale();
  const viewName = useViewName();
  const { permissions } = useVendorDirectoryPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = searchParams.get("days") || "30";
  const canExport = hasVendorDirectoryPermission(permissions, P.ANALYTICS_EXPORT);
  const [exportMessage, setExportMessage] = useState("");
  const [exportError, setExportError] = useState("");
  const [exporting, setExporting] = useState(false);

  const loader = useCallback(async () => {
    const body = await getExecutiveAnalyticsDashboard(clientViewId, { days });
    return body.dashboard;
  }, [clientViewId, days]);

  const { dashboard, loading, errorMessage, disabled, load } = useAnalyticsLoad(loader);

  const onDaysChange = (event) => {
    const next = new URLSearchParams(searchParams);
    next.set("days", event.target.value);
    setSearchParams(next, { replace: true });
  };

  const onExport = async () => {
    setExporting(true);
    setExportMessage("");
    setExportError("");
    try {
      await createAnalyticsExport(clientViewId, {
        exportType: "executive_summary",
        filters: { days: Number(days) },
      });
      setExportMessage(t(`${PREFIX}.exportSuccess`));
    } catch (error) {
      setExportError(error.message || t(`${PREFIX}.exportError`));
    } finally {
      setExporting(false);
    }
  };

  const guideItems = [
    { key: "range", label: t(`${PREFIX}.actions.range`) },
    canExport ? { key: "export", label: t(`${PREFIX}.actions.export`) } : null,
    { key: "coverage", label: t(`${PREFIX}.actions.coverage`), to: `${serviciosBase}/analytics/cobertura` },
    { key: "queues", label: t(`${PREFIX}.actions.queues`), to: `${serviciosBase}/analytics/colas` },
  ].filter(Boolean);

  return (
    <AnalyticsShell>
      <AnalyticsHeader
        title={t(`${PREFIX}.executive.title`)}
        intro={t(`${PREFIX}.executive.intro`, { viewName })}
        actions={
          <div className="cvd-analytics-hub__hero-actions">
            <label className="cvd-analytics-hub__field">
              <span>{t(`${PREFIX}.rangeLabel`)}</span>
              <select value={days} onChange={onDaysChange} aria-label={t(`${PREFIX}.rangeAria`)}>
                <option value="7">{t(`${PREFIX}.range.7`)}</option>
                <option value="30">{t(`${PREFIX}.range.30`)}</option>
                <option value="90">{t(`${PREFIX}.range.90`)}</option>
              </select>
            </label>
            {canExport ? (
              <button type="button" className="btn" onClick={onExport} disabled={exporting || disabled || loading}>
                {exporting ? t(`${PREFIX}.exporting`) : t(`${PREFIX}.export`)}
              </button>
            ) : null}
          </div>
        }
      />

      {exportMessage ? <div className="cvd-alert cvd-alert--success">{exportMessage}</div> : null}
      {exportError ? <div className="cvd-alert cvd-alert--warning">{exportError}</div> : null}

      <PageState loading={loading} errorMessage={errorMessage} disabled={disabled} onRetry={load}>
        {dashboard ? (
          <>
            <ActionGuide items={guideItems} />

            {dashboard.freshness?.isStale ? (
              <div className="cvd-alert cvd-alert--warning" role="status">
                {t(`${PREFIX}.stale`)}{" "}
                {translateEnum(t, "staleReason", dashboard.freshness.staleReason, "")}
              </div>
            ) : null}

            <div className="cvd-metric-grid">
              <MetricCard label={t(`${PREFIX}.kpis.publishedVendors`)} value={dashboard.kpis.publishedVendors} />
              <MetricCard label={t(`${PREFIX}.kpis.verifiedVendors`)} value={dashboard.kpis.verifiedVendors} />
              <MetricCard label={t(`${PREFIX}.kpis.claimedVendors`)} value={dashboard.kpis.claimedVendors} />
              <MetricCard label={t(`${PREFIX}.kpis.contactEvents`)} value={dashboard.kpis.contactEvents} />
              <MetricCard label={t(`${PREFIX}.kpis.leadRequests`)} value={dashboard.kpis.leadRequests} />
              <MetricCard label={t(`${PREFIX}.kpis.verifiedCompletedJobs`)} value={dashboard.kpis.verifiedCompletedJobs} />
              <MetricCard label={t(`${PREFIX}.kpis.publishedReviews`)} value={dashboard.kpis.publishedReviews} />
              <MetricCard label={t(`${PREFIX}.kpis.substantiatedComplaints`)} value={dashboard.kpis.substantiatedComplaints} />
            </div>

            <div className="cvd-metric-grid">
              <RateCard
                t={t}
                label={t(`${PREFIX}.rates.vendorLeadAcceptanceRate`)}
                rate={dashboard.rates?.vendorLeadAcceptanceRate}
              />
              <RateCard
                t={t}
                label={t(`${PREFIX}.rates.substantiatedComplaintRate`)}
                rate={dashboard.rates?.substantiatedComplaintRate}
              />
            </div>

            <div className="cvd-panel">
              <h2>{t(`${PREFIX}.funnel.title`)}</h2>
              <p className="cm-muted">{t(`${PREFIX}.funnel.note`)}</p>
              <table className="cvd-table">
                <caption className="sr-only">{t(`${PREFIX}.funnel.caption`)}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t(`${PREFIX}.funnel.stage`)}</th>
                    <th scope="col">{t(`${PREFIX}.funnel.count`)}</th>
                    <th scope="col">{t(`${PREFIX}.funnel.source`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboard.funnel || []).map((stage) => (
                    <tr key={stage.stage}>
                      <td>{translateEnum(t, "funnel.stages", stage.stage, stage.label)}</td>
                      <td>{stage.count}</td>
                      <td>
                        <code>{stage.source}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {dashboard.healthWarnings?.length ? (
              <div className="cvd-panel">
                <h2>{t(`${PREFIX}.warnings.title`)}</h2>
                <ul className="cvd-analytics-hub__warning-list">
                  {dashboard.healthWarnings.map((item) => {
                    const href = workspaceHref(serviciosBase, WARNING_PATHS[item.key]);
                    return (
                      <li key={item.key}>
                        <div>
                          <strong>{translateEnum(t, "warnings.items", item.key, item.key)}</strong>
                          <span>{t(`${PREFIX}.warnings.count`, { count: item.count })}</span>
                        </div>
                        {href ? (
                          <Link className="btn" to={href}>
                            {t(`${PREFIX}.warnings.open`)}
                          </Link>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {dashboard.coverageGaps?.length ? (
              <div className="cvd-panel">
                <div className="cvd-analytics-hub__panel-head">
                  <h2>{t(`${PREFIX}.gaps.title`)}</h2>
                  <Link className="btn" to={`${serviciosBase}/expansion`}>
                    {t(`${PREFIX}.gaps.openExpansion`)}
                  </Link>
                </div>
                <p className="cm-muted">{t(`${PREFIX}.gaps.intro`)}</p>
                <table className="cvd-table">
                  <caption className="sr-only">{t(`${PREFIX}.gaps.caption`)}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t(`${PREFIX}.coverage.category`)}</th>
                      <th scope="col">{t(`${PREFIX}.coverage.municipality`)}</th>
                      <th scope="col">{t(`${PREFIX}.coverage.published`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.coverageGaps.map((row) => (
                      <tr key={`${row.category_id}-${row.municipality_id}`}>
                        <td>{row.category_name}</td>
                        <td>{row.municipality_name}</td>
                        <td>{row.published_vendors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
      </PageState>
    </AnalyticsShell>
  );
}

function CoveragePage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t } = useLocale();
  const viewName = useViewName();

  const loader = useCallback(async () => {
    const body = await getCoverageAnalyticsDashboard(clientViewId);
    return body.dashboard;
  }, [clientViewId]);

  const { dashboard, loading, errorMessage, disabled, load } = useAnalyticsLoad(loader);
  const rows = dashboard?.matrix || [];

  return (
    <AnalyticsShell>
      <AnalyticsHeader
        title={t(`${PREFIX}.coverage.title`)}
        intro={t(`${PREFIX}.coverage.intro`, { viewName })}
        actions={
          <div className="cvd-analytics-hub__hero-actions">
            <Link className="btn btn-primary" to={`${serviciosBase}/expansion`}>
              {t(`${PREFIX}.coverage.openExpansion`)}
            </Link>
          </div>
        }
      />
      <PageState loading={loading} errorMessage={errorMessage} disabled={disabled} onRetry={load}>
        {dashboard ? (
          <>
            <ActionGuide
              items={[
                { key: "coverage", label: t(`${PREFIX}.actions.coverage`) },
                { key: "expansion", label: t(`${PREFIX}.actions.expansion`), to: `${serviciosBase}/expansion` },
              ]}
            />
            <div className="cvd-panel">
              <p className="cm-muted">
                {t(`${PREFIX}.coverage.target`, { count: dashboard.coverageTarget })}
              </p>
              {rows.length ? (
                <table className="cvd-table">
                  <caption className="sr-only">{t(`${PREFIX}.coverage.caption`)}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t(`${PREFIX}.coverage.category`)}</th>
                      <th scope="col">{t(`${PREFIX}.coverage.municipality`)}</th>
                      <th scope="col">{t(`${PREFIX}.coverage.published`)}</th>
                      <th scope="col">{t(`${PREFIX}.coverage.verified`)}</th>
                      <th scope="col">{t(`${PREFIX}.coverage.status`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.category_id}-${row.municipality_id}`}>
                        <td>{row.category_name}</td>
                        <td>
                          {row.displayState === "suppressed"
                            ? t(`${PREFIX}.coverage.suppressed`)
                            : row.municipality_name}
                        </td>
                        <td>{row.published_vendors}</td>
                        <td>{row.verified_vendors}</td>
                        <td>{translateEnum(t, "coverage.statusValues", row.coverage_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="cm-muted">{t(`${PREFIX}.coverage.empty`)}</p>
              )}
            </div>
          </>
        ) : null}
      </PageState>
    </AnalyticsShell>
  );
}

function LeadsPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t } = useLocale();
  const viewName = useViewName();

  const loader = useCallback(async () => {
    const body = await getLeadAnalyticsDashboard(clientViewId, { days: 30 });
    return body.dashboard;
  }, [clientViewId]);

  const { dashboard, loading, errorMessage, disabled, load } = useAnalyticsLoad(loader);

  return (
    <AnalyticsShell>
      <AnalyticsHeader
        title={t(`${PREFIX}.leads.title`)}
        intro={t(`${PREFIX}.leads.intro`, { viewName })}
        actions={
          <div className="cvd-analytics-hub__hero-actions">
            <Link className="btn" to={`${serviciosBase}/analytics/colas`}>
              {t(`${PREFIX}.leads.openQueues`)}
            </Link>
          </div>
        }
      />
      <PageState loading={loading} errorMessage={errorMessage} disabled={disabled} onRetry={load}>
        {dashboard ? (
          <>
            <ActionGuide
              items={[
                { key: "leads", label: t(`${PREFIX}.actions.leads`) },
                { key: "queues", label: t(`${PREFIX}.actions.queues`), to: `${serviciosBase}/analytics/colas` },
              ]}
            />
            <div className="cvd-metric-grid">
              <MetricCard label={t(`${PREFIX}.leads.requests`)} value={dashboard.funnel?.requests?.lead_requests} />
              <MetricCard
                label={t(`${PREFIX}.leads.deliveries`)}
                value={dashboard.funnel?.deliveries?.deliveries_success}
              />
              <MetricCard
                label={t(`${PREFIX}.leads.jobs`)}
                value={dashboard.funnel?.completions?.verified_completed_jobs}
              />
            </div>
            <RateCard t={t} label={t(`${PREFIX}.leads.acceptance`)} rate={dashboard.rates?.acceptanceRate} />
            <p className="cm-muted">
              {t(`${PREFIX}.leads.readiness`, {
                status: translateEnum(t, "leads.readinessValues", dashboard.rankingReadiness?.status),
              })}
            </p>
          </>
        ) : null}
      </PageState>
    </AnalyticsShell>
  );
}

function QueuesPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t } = useLocale();
  const viewName = useViewName();

  const loader = useCallback(async () => {
    const body = await getQueueAnalyticsDashboard(clientViewId);
    return body.dashboard;
  }, [clientViewId]);

  const { dashboard, loading, errorMessage, disabled, load } = useAnalyticsLoad(loader);
  const queues = Object.entries(dashboard?.queues || {});

  return (
    <AnalyticsShell>
      <AnalyticsHeader
        title={t(`${PREFIX}.queues.title`)}
        intro={t(`${PREFIX}.queues.intro`, { viewName })}
      />
      <PageState loading={loading} errorMessage={errorMessage} disabled={disabled} onRetry={load}>
        {dashboard ? (
          <>
            <ActionGuide items={[{ key: "queues", label: t(`${PREFIX}.actions.queues`) }]} />
            {queues.length ? (
              <table className="cvd-table">
                <caption className="sr-only">{t(`${PREFIX}.queues.caption`)}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t(`${PREFIX}.queues.queue`)}</th>
                    <th scope="col">{t(`${PREFIX}.queues.openCount`)}</th>
                    <th scope="col">{t(`${PREFIX}.queues.action`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {queues.map(([key, count]) => {
                    const href = workspaceHref(serviciosBase, QUEUE_PATHS[key]);
                    return (
                      <tr key={key}>
                        <td>{translateEnum(t, "queues.labels", key)}</td>
                        <td>{count}</td>
                        <td className="cvd-table__col--actions">
                          {href ? (
                            <Link className="btn" to={href}>
                              {t(`${PREFIX}.queues.open`)}
                            </Link>
                          ) : (
                            <span className="cm-muted">{t(`${PREFIX}.queues.noLink`)}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="cm-muted">{t(`${PREFIX}.queues.empty`)}</p>
            )}
          </>
        ) : null}
      </PageState>
    </AnalyticsShell>
  );
}

export default function ClientVendorDirectoryAnalyticsHubPage() {
  const { serviciosBase } = useVendorDirectoryPaths();
  const base = `${serviciosBase}/analytics`;

  return (
    <Routes>
      <Route index element={<ExecutiveOverviewPage />} />
      <Route path="cobertura" element={<CoveragePage />} />
      <Route path="solicitudes" element={<LeadsPage />} />
      <Route path="colas" element={<QueuesPage />} />
      <Route path="*" element={<Navigate to={base} replace />} />
    </Routes>
  );
}
